import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import FeatureBlock from '@/components/feature-block'
import PrismicLink from '@/components/prismic-link'
import RichText from '@/components/rich-text'
import {Text} from '@/components/text'
import {usePageInfo} from '@/providers/page-info'
import {BackgroundWrapper, Section} from '@/styles/grid'
import {getVideoSources} from '@/utils/get-video-sources'
import {isValidBackgroundColor} from '@/utils/is-valid-background-color'
import mq from '@/utils/media-query'

import type {Props as ButtonProps} from '@/components/button'
import type {Props as RichTextProps} from '@/components/rich-text'
import type {TextProps} from '@/components/text'
import type {
    FeatureBlockSliceFragment,
    FeatureBlockFragment,
    PartnerTemplateFeatureBlockFragment,
} from '@/graphql-types'
import type {StateProps} from '@/hooks/use-navigate'
import type {BackgroundColor} from '@/utils/is-valid-background-color'

type Props = {
    slice: FeatureBlockSliceFragment | FeatureBlockFragment | PartnerTemplateFeatureBlockFragment
    dataTestId?: string
}

export const FeatureBlockSlice = React.forwardRef<HTMLDivElement, Props>(
    ({slice, dataTestId}, ref) => {
        const data = slice.primary
        const predefined = data.predefined_feature_block.document
        const backgroundColor = getBackgroundColor(slice.primary.background)
        const video = getVideoSources({
            webm: slice.primary.video_webm,
            mp4: slice.primary.video_mp4,
        })

        const {location} = usePageInfo()
        const locationState = location?.state as StateProps
        let description = data.description.richText

        const interpolateVars = (description: RichTextProps['content']) => {
            const interpolation = (text: string | undefined) => {
                let finalText = text
                if (locationState.email) {
                    finalText = finalText?.replace(/{{email}}/gi, locationState.email)
                }

                if (locationState.country) {
                    finalText = finalText?.replace(/{{country}}/gi, locationState.country)
                }

                return finalText
            }

            return (
                Array.isArray(description) &&
                description.map((paragraph) => {
                    if ('text' in paragraph) {
                        return {
                            ...paragraph,
                            text: interpolation(paragraph.text),
                        }
                    }
                    return paragraph
                })
            )
        }

        if (locationState?.email) {
            description = interpolateVars(description)
        }

        return (
            <BackgroundWrapper ref={ref} $color={backgroundColor}>
                <Section data-testid={dataTestId}>
                    {data.title_over_section && (
                        <TitleBeforeSection>
                            <Text $variant="heading-large">{data.title_over_section}</Text>
                        </TitleBeforeSection>
                    )}

                    <FeatureBlock
                        videoId={data.wistia_video_id}
                        inlineVideo={video}
                        ctaLink={data.cta_link}
                        ctaText={data.cta_label}
                        isCircularImage={data.is_circular_image}
                        image={predefined?.data.image ?? data.image}
                        preTitle={predefined?.data.subtitle ?? data.headline}
                        title={predefined?.data.title ?? data.title}
                        body={predefined?.data.body.richText ?? description}
                        mirrored={!!data.inverse}
                        headingLevel="h2"
                        bodyProps={getTextProps(backgroundColor)}
                        dataTestId={dataTestId}
                        titleProps={getTextProps(backgroundColor)}
                        preTitleProps={{$color: backgroundColor === 'grey' ? 'muted' : 'brand'}}
                    >
                        {!!data.pre_cta_links && <RichText content={data.pre_cta_links.richText} />}

                        {data.cta_label && !data.is_circular_image && (
                            <Button
                                $variant={getButtonVariant(backgroundColor)}
                                as={PrismicLink}
                                {...(dataTestId && {'data-testid': `${dataTestId}-button`})}
                                {...data.cta_link}
                            >
                                {data.cta_label}
                            </Button>
                        )}

                        {!!data.cta_hash_link && !!data.cta_label && !data.is_circular_image && (
                            <Button
                                as="a"
                                $variant={getButtonVariant(backgroundColor)}
                                href={`#pleo-page-section-${data.cta_hash_link.replace('#', '')}`}
                                {...(dataTestId && {'data-testid': `${dataTestId}-button`})}
                            >
                                {data.cta_label}
                            </Button>
                        )}
                    </FeatureBlock>
                </Section>
            </BackgroundWrapper>
        )
    },
)

const getBackgroundColor = (maybeBackgroundColor: string): BackgroundColor => {
    switch (maybeBackgroundColor) {
        case 'black':
            return 'tertiary'

        default:
            return isValidBackgroundColor(maybeBackgroundColor) ? maybeBackgroundColor : 'default'
    }
}

const getButtonVariant = (backgroundColor: BackgroundColor): ButtonProps['$variant'] => {
    switch (backgroundColor) {
        case 'tertiary':
            return 'inverted-outline'

        default:
            return 'outline'
    }
}

const getTextProps = (backgroundColor: BackgroundColor): TextProps => {
    switch (backgroundColor) {
        case 'tertiary':
            return {
                $color: 'inverted',
            }

        default:
            return {}
    }
}

const TitleBeforeSection = styled.div`
    padding-bottom: ${({theme}) => theme.spacings.s40};

    ${mq.min('md')} {
        padding-bottom: 80px;
    }
`

export const featureBlockSliceFragment = graphql`
    fragment FeatureBlockSlice on PrismicSlicePageDataBodyFeatureBlock {
        __typename
        primary {
            title_over_section
            title
            inverse
            background
            headline
            description {
                richText
            }
            pre_cta_links {
                richText
            }
            is_circular_image
            cta_label
            predefined_feature_block {
                document {
                    ... on PrismicFeatureBlock {
                        data {
                            image {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                                gatsbyImageData(
                                    breakpoints: $imageBreakpoints
                                    sizes: $twoColumnsAsymmetric
                                )
                            }
                            title
                            subtitle
                            body {
                                richText
                            }
                        }
                    }
                }
            }
            cta_link {
                ...PrismicLink
            }
            cta_hash_link
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            wistia_video_id
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
        }
    }
`
